/**
 * Échelle de clientèle de l'organisation : des tranches d'âge en chiffres (ce qui compte pour le calcul) et/ou un
 * titre de la liste commune (l'étiquette : Enfant, Aîné ; ou un statut : Étudiant, Résident…). Modèles de départ,
 * vérification des chevauchements et des trous, demande d'ajout d'un titre manquant.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { requestTariffListItem, saveTariffScale } from '../../api'
import { errorText, numOrNull, type TabProps } from './common'

type Row = { key: string; id?: number; category_id: number | null; age_min: string; age_max: string }

const PRESETS: { key: string; rows: [string, number | null, number | null][] }[] = [
  { key: 'preset_cinq', rows: [['bebe', 0, 5], ['enfant', 6, 14], ['jeune', 15, 17], ['adulte', 18, 59], ['aine', 60, null]] },
  { key: 'preset_trois', rows: [['jeune', 0, 17], ['adulte', 18, 64], ['aine', 65, null]] },
  { key: 'preset_sepaq', rows: [['enfant', 0, 17], ['adulte', 18, null]] },
]

let seq = 0
const newKey = () => `r${++seq}`

export function ScaleTab({ orgId, data, onSaved, onListChanged }: TabProps) {
  const { t } = useTranslation('gestion')
  const titles = data.lists.categories
  const [rows, setRows] = useState<Row[]>(() =>
    data.scale.map((c) => ({ key: newKey(), id: c.id, category_id: c.category_id, age_min: c.age_min?.toString() ?? '', age_max: c.age_max?.toString() ?? '' })),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newTitle, setNewTitle] = useState('')
  const [newKind, setNewKind] = useState('age')

  const set = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    if (rows.length && !window.confirm(t('tarifs.remplacer_echelle'))) return
    const bySlug = new Map(titles.map((x) => [x.slug, x.id]))
    setRows(preset.rows.map(([slug, lo, hi]) => ({ key: newKey(), category_id: bySlug.get(slug) ?? null, age_min: lo?.toString() ?? '', age_max: hi?.toString() ?? '' })))
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const next = await saveTariffScale(orgId, rows.map((r) => ({ id: r.id, category_id: r.category_id, age_min: numOrNull(r.age_min), age_max: numOrNull(r.age_max) })))
      onSaved(next)
      setRows(next.scale.map((c) => ({ key: newKey(), id: c.id, category_id: c.category_id, age_min: c.age_min?.toString() ?? '', age_max: c.age_max?.toString() ?? '' })))
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  const ask = async () => {
    if (!newTitle.trim()) return
    setBusy(true)
    try {
      const item = await requestTariffListItem(orgId, 'categories', newTitle.trim(), { kind: newKind })
      onListChanged?.()
      setRows((rs) => [...rs, { key: newKey(), category_id: item.id, age_min: '', age_max: '' }])
      setNewTitle('')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <p className="g-muted">{t('tarifs.echelle_aide')}</p>
      <div className="g-chips">
        <span className="g-muted">{t('tarifs.modeles')}</span>
        {PRESETS.map((p) => (
          <button key={p.key} type="button" className="g-chip" onClick={() => applyPreset(p)}>
            {t(`tarifs.${p.key}`)}
          </button>
        ))}
      </div>
      <table className="g-table g-tarifs__table">
        <thead>
          <tr>
            <th>{t('tarifs.titre_col')}</th>
            <th>{t('tarifs.age_min')}</th>
            <th>{t('tarifs.age_max')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                <select className="g-input" value={r.category_id ?? ''} onChange={(e) => set(r.key, { category_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{t('tarifs.sans_titre')}</option>
                  {(['age', 'status', 'group'] as const).map((kind) => (
                    <optgroup key={kind} label={t(`tarifs.genre_${kind}`)}>
                      {titles
                        .filter((x) => x.kind === kind)
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                            {x.pending ? ` (${t('tarifs.en_attente')})` : ''}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
              </td>
              <td>
                <input className="g-input g-tarifs__num" inputMode="numeric" value={r.age_min} onChange={(e) => set(r.key, { age_min: e.target.value })} />
              </td>
              <td>
                <input className="g-input g-tarifs__num" inputMode="numeric" value={r.age_max} placeholder={t('tarifs.et_plus')} onChange={(e) => set(r.key, { age_max: e.target.value })} />
              </td>
              <td>
                <button type="button" className="g-link g-danger" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                  {t('commun.supprimer')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="g-editor__buttons">
        <button type="button" className="btn" onClick={() => setRows((rs) => [...rs, { key: newKey(), category_id: null, age_min: '', age_max: '' }])}>
          <span className="mdi mdi-plus" aria-hidden /> {t('tarifs.ajouter_categorie')}
        </button>
        <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void save()}>
          {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}
      {data.scale_problems.length > 0 && (
        <ul className="g-alert g-tarifs__problems">
          {data.scale_problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="g-tarifs__request">
        <span className="g-muted">{t('tarifs.titre_manquant')}</span>
        <input className="g-input" value={newTitle} placeholder={t('tarifs.nouveau_titre')} onChange={(e) => setNewTitle(e.target.value)} />
        <select className="g-input" value={newKind} onChange={(e) => setNewKind(e.target.value)}>
          {(['age', 'status', 'group'] as const).map((k) => (
            <option key={k} value={k}>
              {t(`tarifs.genre_${k}`)}
            </option>
          ))}
        </select>
        <button type="button" className="btn" disabled={busy || !newTitle.trim()} onClick={() => void ask()}>
          {t('tarifs.demander')}
        </button>
      </div>
    </>
  )
}
